import {
  ApprovedStatusType,
  Availability,
  Currency,
  DayPortion,
  EmploymentStatus,
  EnglishLevel,
  EvaluatorKey,
  ExpenseCategory,
  ExpenseStatus,
  Gender,
  HistoryType,
  InterviewEmailStatus,
  InterviewQuestionType,
  InventoryStatus,
  InventoryApprovalStatus,
  InventoryAccessCredentialType,
  InventoryAuditAction,
  LeaveType,
  NoteCategory,
  NotificationType,
  Permission,
  PositionModel,
  ProjectPriority,
  RoleType,
  ScoringRuleType,
  Seniority,
  Status,
  WorkPlanEntryType,
  WorkType,
} from './enum.js';

// =====================
// Chatbot SSE Types
// =====================

/**
 * The single source of truth for every language the product supports.
 *
 * Client i18n, server-generated text (emails, chatbot, report exports) and the chatbot
 * all derive their supported-language lists from here. To add a language, edit this array
 * only — do not redeclare the list anywhere else.
 */
export const SUPPORTED_LANGUAGES = ['tr', 'en', 'de', 'el', 'fr'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Default language used as the fallback when a value cannot be resolved. */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'tr';

/**
 * Normalize an arbitrary language value (e.g. 'de-DE', 'EN', undefined) to a supported
 * language, falling back to {@link DEFAULT_LANGUAGE} for anything unrecognized.
 */
export function normalizeLanguage(value?: string | null): SupportedLanguage {
  const short = (value ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(short)
    ? (short as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

// --- Chatbot aliases (kept for backward compatibility; same list as SUPPORTED_LANGUAGES) ---

export const CHAT_LOCALES = SUPPORTED_LANGUAGES;

export type ChatLocale = SupportedLanguage;

export function normalizeChatLocale(locale?: string | null): ChatLocale {
  return normalizeLanguage(locale);
}

const TURKISH_CHAR_MAP: Record<string, string> = {
  Ç: 'C',
  Ğ: 'G',
  İ: 'I',
  I: 'I',
  Ö: 'O',
  Ş: 'S',
  Ü: 'U',
  ç: 'c',
  ğ: 'g',
  ı: 'i',
  i: 'i',
  ö: 'o',
  ş: 's',
  ü: 'u',
};

export function normalizeTurkishText(value: string): string {
  return value
    .replace(/[ÇĞİIÖŞÜçğıiöşü]/g, (char) => TURKISH_CHAR_MAP[char] ?? char)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export type PageContextType =
  | 'DASHBOARD'
  | 'CANDIDATE_LIST'
  | 'CANDIDATE_DETAIL'
  | 'EMPLOYEE_LIST'
  | 'EMPLOYEE_DETAIL'
  | 'LEAVE_REQUESTS'
  | 'EXPENSES'
  | 'POSITIONS'
  | 'SETTINGS'
  | 'OTHER';

/**
 * Page context for context-aware sidebar copilot.
 * Allows the chatbot to understand where the user is in the application.
 *
 * Note: This interface could be modeled as a discriminated union to enforce
 * that entityId is required for *_DETAIL pages, but for backward compatibility
 * and flexibility, we use optional fields with documentation.
 *
 * @example
 * // List page context
 * const listContext: PageContext = {
 *   page: 'CANDIDATE_LIST',
 *   visibleIds: [1, 2, 3],
 *   filters: { status: 'ACTIVE' }
 * };
 *
 * @example
 * // Detail page context (entityId should be provided)
 * const detailContext: PageContext = {
 *   page: 'CANDIDATE_DETAIL',
 *   entityId: 123,
 *   entityName: 'John Doe'
 * };
 */
export interface PageContext {
  /** Current page type */
  page: PageContextType;
  /**
   * Entity ID when viewing a detail page.
   * Note: Should be provided when page ends with '_DETAIL' (e.g., CANDIDATE_DETAIL, EMPLOYEE_DETAIL).
   * Required for context-aware responses on detail pages.
   */
  entityId?: string;
  /** Visible entity IDs on list pages */
  visibleIds?: string[];
  /** Applied filters on list pages */
  filters?: Record<string, unknown>;
  /** Current search query */
  searchQuery?: string;
  /** Entity name for context (e.g., candidate name, employee name) */
  entityName?: string;
}

export type SSEEventType =
  | 'start'
  | 'thinking'
  | 'tool_call'
  | 'tool_result'
  | 'delta'
  | 'final'
  | 'error'
  | 'action_start'
  | 'action_result';

/**
 * UI component types for the Generative UI pattern.
 * Maps to React components in the ChatBot/ToolResultCards directory.
 */
export type ToolUIComponentType =
  | 'leave_balance_card'
  | 'leave_requests_list'
  | 'leave_summary_card'
  | 'expense_list'
  | 'expense_summary_card'
  | 'dashboard_stats_card'
  | 'pending_approvals_card'
  | 'candidate_list'
  | 'candidate_card'
  | 'candidate_recommendation_card'
  | 'employee_list'
  | 'employee_card'
  | 'position_card'
  | 'position_list_card'
  | 'org_structure_card'
  | 'policy_result_card'
  | 'policy_list_card'
  | 'interview_questions_card'
  | 'chart'
  | 'comparison_table'
  | 'timeline'
  | 'skill_matrix'
  | 'skill_list_card'
  | 'hiring_funnel_card'
  | 'pipeline_overview_card'
  | 'trend_chart_card'
  | 'overtime_list'
  | 'document_checklist'
  | 'text';

export interface ToolUIComponent {
  type: ToolUIComponentType;
  props: Record<string, unknown>;
}

export interface StreamStartEvent {
  conversationId: string;
  messageId: string;
}

export interface StreamThinkingEvent {
  text: string;
}

export interface StreamToolCallEvent {
  toolCallId: string;
  name: string;
  arguments: Record<string, unknown>;
  /** Expected UI component type for skeleton rendering while tool executes */
  uiComponentType?: ToolUIComponentType;
}

export interface StreamToolResultEvent {
  toolCallId: string;
  toolName: string;
  success: boolean;
  data?: unknown;
  uiComponent?: ToolUIComponent;
}

export interface StreamDeltaEvent {
  content: string;
}

export interface StreamFinalEvent {
  messageId: string;
  content: string;
  toolCalls: ToolCallRecord[] | null;
  suggestions?: string[];
}

export interface StreamErrorEvent {
  code: string;
  message: string;
  /** i18n key for frontend translation */
  messageKey?: string;
  /** Parameters for message interpolation */
  params?: Record<string, unknown>;
}

/** SSE event emitted when a card action starts executing */
export interface StreamActionStartEvent {
  actionId: string;
  cardInstanceId: string;
  actionType: string;
}

/** SSE event emitted when a card action finishes executing */
export interface StreamActionResultEvent {
  actionId: string;
  success: boolean;
  cardPatch?: Record<string, unknown>;
  toast?: { type: 'success' | 'error'; message: string };
}

export interface ToolCallRecord {
  name: string;
  arguments: Record<string, unknown>;
  result: string;
  /** Human-readable tool summary. Prefer this over parsing formatted result text. */
  summary?: string;
  uiComponent?: ToolUIComponent;
  status: 'SUCCESS' | 'BLOCKED' | 'ERROR' | 'TIMEOUT';
  durationMs: number;
  error?: string;
}

// Tool Result Props Types
export interface LeaveBalanceCardProps {
  manualBalanceMinutes?: number;
  seniorityEntitlementMinutes?: number;
  totalEntitlementMinutes?: number;
  usedMinutes?: number;
  remainingMinutes?: number;
  summary?: string;
  error?: string;
}

export interface LeaveRequestItem {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  dayPortion: DayPortion;
  isHourly: boolean;
  startTime: string | null;
  endTime: string | null;
  durationInDays: number;
  status: string;
  reason: string | null;
}

export interface LeaveRequestsListProps {
  requests?: LeaveRequestItem[];
  total?: number;
  summary?: string;
  error?: string;
}

export interface LeaveSummaryCardProps {
  year?: number;
  totalUsedDays?: number;
  byLeaveType?: Record<string, number>;
  remainingAnnual?: number;
  summary?: string;
  error?: string;
}

export interface ExpenseItem {
  id: string;
  category: string;
  amount: number;
  currency: string;
  expenseDate: string;
  status: string;
  description: string | null;
}

export interface ExpenseListProps {
  expenses?: ExpenseItem[];
  total?: number;
  summary?: string;
  error?: string;
}

export interface ExpenseSummaryCardProps {
  period?: string;
  totalExpenses?: number;
  approvedTotal?: number;
  byStatus?: Record<string, { count: number; total: number }>;
  byCategory?: Record<string, number>;
  summary?: string;
  error?: string;
}

export interface DashboardStatsCardProps {
  onboardingCount?: number;
  activeEmployeeCount?: number;
  pendingApprovals?: {
    leave?: number;
    expense?: number;
    overtime?: number;
  };
  pipelineStats?: Record<string, number>;
  summary?: string;
  error?: string;
}

export interface PendingApprovalsCardProps {
  leave?: { count: number; items?: unknown[] };
  expense?: { count: number; items?: unknown[] };
  overtime?: { count: number; items?: unknown[] };
  summary?: string;
  error?: string;
}

export interface CandidateListItemProps {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  position?: string;
  matchScore?: number;
}

export interface CandidateListProps {
  candidates?: CandidateListItemProps[];
  total?: number;
  summary?: string;
  error?: string;
}

export interface CandidateCardProps {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  status?: string;
  experience?: string;
  yearsOfExperience?: number;
  skills?: string[];
  location?: string;
  expectedSalary?: number;
  summary?: string;
  error?: string;
}

export interface CandidateRecommendationProps {
  positionTitle?: string;
  recommendations?: Array<{
    candidateId: string;
    name: string;
    matchScore: number;
    skills: string[];
    experience: string;
  }>;
  summary?: string;
  error?: string;
}

export interface EmployeeListItemProps {
  id: string;
  firstName: string;
  lastName: string;
  department?: string;
  title?: string;
}

export interface EmployeeListProps {
  employees?: EmployeeListItemProps[];
  total?: number;
  summary?: string;
  error?: string;
}

export interface EmployeeCardProps {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  department?: string;
  title?: string;
  hireDate?: string;
  phone?: string;
  location?: string;
  summary?: string;
  error?: string;
}

export interface PositionCardProps {
  id?: string;
  title?: string;
  department?: string;
  status?: string;
  headcount?: number;
  filledCount?: number;
  location?: string;
  summary?: string;
  error?: string;
}

export interface PositionListItem {
  id: string;
  title: string;
  company: string | null;
  status: string;
  candidateCount: number;
}

export interface PositionListCardProps {
  positions?: PositionListItem[];
  total?: number;
  summary?: string;
  error?: string;
}

export interface OrgNodeProps {
  id: string;
  name: string;
  title: string;
  children?: OrgNodeProps[];
}

export interface OrgStructureCardProps {
  structure?: OrgNodeProps[];
  summary?: string;
  error?: string;
}

export interface Employee {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  companyPhone: string | null;
  department: string | null;
  hireDate: string;
  salary: string;
  availability: Availability;
  location: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  type: string;
  birthday: string;
  nationalId: string;
  address: string;
  iban: string;
  mealCardNumber: string | null;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactType: string;
  terminationDate: string | null;
  // Latest employment period's status; present on the detail (getById) payload. Lets the
  // UI make an INACTIVE employee read-only and offer re-hire. Absent on list payloads.
  currentEmploymentStatus?: EmploymentStatus | null;
  companyEmail: string | null;
  locationDistrict: string | null;
  companyName: string | null;
  workType: string;
  gender: Gender;
  driversLicense: string | null;
  education: Education[];
  maritalStatus: string;
  employmentType: string;
  supervisorId: string | null;
  leaveDayCount: number | null;
  timesheetRequired: boolean;
  postalCode: string;
  salaryType: string;
  paymentPeriod: string;
  benefits: string | null;
  privateHealthInsuranceNumber: string | null;
  personnelNumber: string | null;
  skillId: string | null;
  companyId: string;
  titleId: string;
  email?: string | null;
  /**
   * Some legacy employee list responses still return a plain role name string,
   * while newer employee detail flows return the full Role object.
   */
  role?: Role | string | null;
  profilePictureUrl?: string | null;
  user: User;
  skills: Skill[];
  title: Title;
  documents: EmployeeDocument[];
  leaveRequests: LeaveRequest[];
  leaveBalance: LeaveBalance;
  remainingMinutes: number;
  groups?: {
    id: string;
    name: string;
    joinedAt?: string | Date | null;
  }[];
  teams?: {
    id: string;
    name: string;
    leaderId?: string | null;
    isLeader?: boolean;
    joinedAt?: string | Date | null;
  }[];
}

export interface LeaveBalance {
  totalEntitlementMinutes: number;
  usedMinutes: number;
  remainingMinutes: number;
}

export interface Education {
  major: string;
  degree: string;
  university: string;
  /** Empty/absent when the education is still ongoing. */
  graduationYear?: string;
  isOngoing?: boolean;
}

export interface User {
  id: string;
  email: string;
  companyEmail: string | null;
  roleId: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  companyId: string | null;
  role: Role | null;
  permissions: PermissionDetails[];
}

export interface EmployeeDocument {
  id: string;
  employeeId: string;
  documentType: string;
  fileName: string | null;
  fileUrl: string | null;
  status: boolean;
  uploadedAt: string | null;
  uploadedBy: string | null;
}

export interface Role {
  id: string;
  name: string;
  description: string | null;
}

export interface PermissionDetails {
  name: string;
}

export interface Skill {
  id: string;
  name: string;
  endorsements?: number | null;
}

export interface LanguageProficiency {
  language: string;
  level: EnglishLevel;
}

export interface LanguageRequirement {
  language: string;
  minLevel: EnglishLevel;
}

export interface Title {
  id: string;
  name: string;
}

export interface CandidateExperience {
  title?: string | null;
  company?: string | null;
  duration?: string | null;
  location?: string | null;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  roleSkills?: string[];
}

export interface CandidateEducation {
  id?: string;
  schoolName?: string | null;
  school?: string | null; // Legacy field support
  universityId?: string | null;
  universityName?: string | null;
  degree?: string | null;
  field?: string | null;
  dates?: string | null;
  activities?: string | null;
  description?: string | null;
  orderIndex?: number;
}

export interface CandidateCertification {
  name: string;
  issuer?: string | null;
}

export interface Candidate {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone?: string;
  skills: Skill[];
  experience: 'JUNIOR' | 'MID' | 'SENIOR';
  yearsOfExperience?: number;
  isActivelyLooking?: boolean;
  isCvExpected?: boolean;
  languageProficiencies?: LanguageProficiency[];
  location?: string;
  expectedSalary?: number;
  notes?: string;
  education?: 'HIGH_SCHOOL' | 'ASSOCIATES' | 'BACHELORS' | 'MASTERS' | 'DOCTORATE';
  availability?: 'IMMEDIATE' | 'TWO_WEEKS' | 'ONE_MONTH' | 'NEGOTIABLE';
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  interviews: Interview[];
  linkedinUrl?: string;
  position?: string;
  respondedAt?: Date;
  schoolName?: string;
  /** Son mezun olunan okul adı (liste API’den gelir) */
  lastGraduatedSchoolName?: string | null;
  graduationStatus?: string;
  activities?: CandidateActivity[];
  status: 'ACTIVE' | 'INTERVIEWING' | 'HIRED' | 'REJECTED';
  poolStatus?: 'AVAILABLE' | 'NOT_LOOKING' | 'DO_NOT_CONTACT' | 'EMPLOYED' | null;
  appliedAt: Date;
  lastContact?: Date;
  currentJobPositionsCount: number;
  skillMatchScore?: number;
  title: Title;
  englishLevel: string;
  candidateId?: string;
  candidateCode?: string;
  documents: Documents[];
  experiences?: CandidateExperience[];
  educations?: CandidateEducation[];
  certifications?: CandidateCertification[];
  about?: string | null;
  kvkkStatus?: 'APPROVED' | 'PENDING' | 'EXPIRED' | 'NOT_SENT';
  calendarEvents?: CalendarEvent[];
  interviewResults?: CandidateInterviewResult[];
}

// InterviewQuestionFormat / InterviewQuestionType / InterviewMcqOption live in
// enum.js so both sides can compare against the values, not string literals.

export interface InterviewQuestion {
  id: number;
  questionType: InterviewQuestionType;
  category: string;
  difficulty: string;
  content: string;
  // Open-ended only (recruiter-facing)
  referenceAnswer?: string | null;
  evaluationCriteria?: string | null;
  // MCQ only
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  correctAnswer?: string | null; // "A" | "B" | "C" | "D"
  // Metadata
  peoplyPositionId: string;
  peoplyCandidateId?: string | null;
  positionTitle: string;
  manuallyEdited?: boolean;
  /**
   * How long interviews from this set run for. The value belongs to the set, so
   * every question in one carries the same number; it is read from the questions
   * because that is where the engine stores it.
   *
   * Optional because engine builds before it was exposed omit the field, which is
   * not the same as a set that deliberately has no time limit.
   */
  interviewDurationMinutes?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface InterviewQuestionsResponse {
  questions: InterviewQuestion[];
  total: number;
}

export interface UpdateInterviewQuestionPayload {
  content?: string;
  category?: string;
  difficulty?: string;
  referenceAnswer?: string | null;
  evaluationCriteria?: string | null;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  correctAnswer?: string | null;
}

export interface CreateInterviewSessionResponse {
  /**
   * exam-engine's session id. The engine types it as a Long and Jackson writes
   * it as a JSON number, so the value arriving over the wire is not a string —
   * AssessmentApiService stringifies it at the boundary, which is what makes
   * this declaration true for consumers. The webhook payload has the same quirk
   * and is normalised the same way.
   */
  sessionId: string;
  interviewLink: string;
  /**
   * When the link stops working, as reported by the engine. Optional because
   * engine versions before the tracking work did not return it; Peoply falls
   * back to computing it from the requested validity.
   */
  expiresAt?: string | null;
  /** How many questions the engine put in the session. */
  questionCount?: number | null;
  /**
   * The interview length the set was generated for. Null when the engine has no
   * record of it, in which case the caller must present a figure as approximate
   * rather than stating one.
   */
  durationMinutes?: number | null;
  /** True when the engine used the candidate's own set rather than the position pool. */
  candidateSpecific?: boolean | null;
}

/**
 * Where one interview session stands, as exam-engine reports it.
 *
 * The engine has no expired state — expiry is derived from `expiresAt` against
 * the current time, so the two sides cannot disagree about when it happened.
 */
export interface InterviewSessionStatus {
  sessionId: string;
  /** PENDING, IN_PROGRESS or COMPLETED. Empty when the engine sent something unrecognised. */
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
}

/**
 * What Peoply's own endpoint returns: the engine's answer plus whether Peoply
 * managed to record the send.
 *
 * `tracked: false` means the interview exists and the link works, but nothing
 * was written on this side — the caller should surface that separately rather
 * than treating the send as failed.
 */
export interface CreateInterviewSessionResult extends CreateInterviewSessionResponse {
  tracked: boolean;
  /**
   * Whether the duration was also stored as the question set's default, for the
   * interviews sent after this one. Null when the send did not ask to change it.
   *
   * false means this interview has the duration that was asked for but the set
   * kept its old one — the send worked, the default did not stick.
   */
  durationDefaultSaved: boolean | null;
  /**
   * What happened to the invitation email. FAILED means the interview is live
   * and the link works but the candidate was not told — the caller has to say
   * so rather than reporting the send as failed.
   */
  emailStatus: InterviewEmailStatus;
}

/**
 * One graded answer as Assessment reports it on the INTERVIEW_COMPLETED webhook.
 * Only questionId and questionType are guaranteed; the rest depend on the
 * question type and on how far grading got, so consumers must handle absence.
 */
export interface InterviewAnswer {
  questionId: number;
  questionType: InterviewQuestionType;
  questionContent?: string;
  // MCQ only
  selectedOption?: string;
  isCorrect?: boolean;
  // Open-ended only
  openText?: string;
  aiScore?: number;
  aiRationale?: string;
  gradingStatus?: string;
}

export interface CandidateInterviewResult {
  id: string;
  sessionId: string;
  candidateId: string;
  /** NOT NULL in the database — every result is tied to the position it was sat for. */
  positionId: string;
  mcqScore: number | null;
  openEndedScore: number | null;
  overallScore: number | null;
  completedAt: string | null;
  answers: InterviewAnswer[];
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  category: 'INTERVIEW' | 'MEETING' | 'REMINDER' | 'CALL' | 'OTHER';
  status: 'SCHEDULED' | 'CANCELLED' | 'COMPLETED' | 'RESCHEDULED';
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  clientName: string;
  companyId?: string | null;
  status: 'PLANNED' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED';
  startDate?: Date;
  endDate?: Date;
  budget?: number;
  skillsRequired: string[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  members?: ProjectMember[];
}

export interface ProjectMember {
  id: string;
  projectId: string;
  employeeId: string;
  role?: string;
  startDate: string;
  endDate?: string;
  hourlyRate?: number;
  createdAt: string;
  updatedAt: string;
  project: Project;
  employee?: Employee;
}

export interface Interview {
  id: string;
  candidateId: string;
  candidateName?: string;
  positionId: string;
  positionTitle?: string;
  startTime: string;
  endTime: string;
  type: 'ONLINE' | 'IN_PERSON';
  status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
  location?: string;
  meetingLink?: string;
  notes?: string;
  googleEventId?: string;
  interviewers: { id: string; name: string; email: string }[];
  createdAt: string;
  updatedAt?: string;
}

export interface Documents {
  id: string;
  leaveRequestId: string;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  type: LeaveType;
  startDate: Date;
  endDate: Date;
  reason?: string | null;
  status: ApprovedStatusType;
  rejectionReason?: string | null;
  uploadedBy?: string | null;
  approvedBy?: string | null;
  comments?: string | null;
  createdAt: Date;
  updatedAt: Date;
  dayPortion: DayPortion;
  startDayPortion?: DayPortion | null;
  endDayPortion?: DayPortion | null;
  otherLeaveTypeReason?: string;
  employee: Employee;
  documents: Documents[];
  teams?: { id: string; name: string }[];
  isHourly: boolean;
  startTime?: string | null;
  endTime?: string | null;
  durationInDays?: number;
}

export interface CandidateActivity {
  id: string;
  candidateId: string;
  userId: string;
  activityType: string;
  content: string;
  createdAt: Date;
  user?: {
    id: string;
    email: string;
  };
}

// A company asset (Inventory model). Access credentials are NOT included here — the
// decrypted secret is only ever fetched through the dedicated, audited endpoint.
export interface InventoryItem {
  id: string;
  companyId: string;
  typeKey: string;
  name: string;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  assetTag: string | null;
  description: string | null;
  photoUrl: string | null;
  photoFileName: string | null;
  // Type-specific attributes keyed by INVENTORY_TYPE_FIELDS[typeKey] (e.g. SIM card
  // phoneNumber/operator, company car plate/mileage). Empty/absent for plain types.
  customFields: Record<string, string | number | null> | null;
  hasDefect: boolean;
  defectNote: string | null;
  status: InventoryStatus;
  approvalStatus: InventoryApprovalStatus;
  assignedToEmployeeId: string | null;
  deliveryDate: string | null;
  deliveryConfirmedAt: string | null;
  returnedAt: string | null;
  createdByEmployeeId: string | null;
  createdByHr: boolean;
  rejectionNote: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// A file attached to an inventory item. The binary lives in object storage; the URL
// for download is issued on demand as a short-lived presigned link.
export interface InventoryAttachmentItem {
  id: string;
  inventoryId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadedByEmployeeId: string | null;
  createdAt: string;
}

// A resolved inventory type (seeded default or company custom).
export interface InventoryTypeItem {
  id: string;
  companyId: string | null;
  key: string;
  label: string;
  sortOrder: number;
  isSystem: boolean;
}

// One entry in an asset's chain of custody (InventoryAssignment).
export interface InventoryAssignmentItem {
  id: string;
  inventoryId: string;
  employeeId: string;
  assignedAt: string;
  assignedBy: string | null;
  confirmedAt: string | null;
  returnedAt: string | null;
  returnedBy: string | null;
  note: string | null;
}

// An access credential WITHOUT the secret — safe to list. The plaintext secret is
// returned only from the audited "reveal" endpoint, never in listings.
export interface InventoryAccessCredentialSummary {
  id: string;
  inventoryId: string;
  credentialType: InventoryAccessCredentialType;
  username: string | null;
  label: string | null;
  hasSecret: boolean;
  createdByEmployeeId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryAuditEventItem {
  id: string;
  inventoryId: string;
  action: InventoryAuditAction;
  actorEmployeeId: string | null;
  fromStatus: InventoryStatus | null;
  toStatus: InventoryStatus | null;
  note: string | null;
  createdAt: string;
}

export interface JobPosition {
  id: string;
  titleId: string;
  requestNo?: string | null;
  customerDisplayName: string | null;
  summary: string;
  description: string | null;
  seniority: Seniority;
  status: Status;
  headcount: number;
  workType: WorkType;
  positionModel: PositionModel;
  locations: string[];
  benefits: string[];
  educationLevel: string | null;
  minYearsOfExperience: number | null;
  languageProficiencies: LanguageRequirement[];
  interviewProcess: string | null;
  interviewDetails: string | null;
  importantNotes: string | null;
  deadline: string | null;
  requiredSkills: Skill[];
  preferredSkills: Skill[];
  contactPerson: ContactPerson[] | null;
  customerContactInfo: string | null;
  salaryRangeMin: number;
  salaryRangeMax: number;
  currency: Currency;
  customerCompanyId?: string;
  isInternalCustomer?: boolean;
  title: Title | string;
  customerCompany?: CustomerCompany;
  companyName?: string;
  groupName?: string | null;
  hasActiveMatchJob?: boolean;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  candidateMatches: CandidateMatch[];
  rules: Rule[];
}

export type MatchRuleType = 'SCORE' | 'PENALTY' | 'FILTER';

export interface RuleDTO {
  evaluatorKey: string;
  type: MatchRuleType;
  weight?: number;
  parameters?: Record<string, unknown> | null;
}

export interface MatchFilterDetail {
  key: string;
  type: 'FILTER';
  passed: boolean;
  meta?: Record<string, unknown>;
}

export interface MatchScoreDetail {
  key: string;
  type: 'SCORE' | 'PENALTY';
  raw: number;
  weight?: number;
  impact?: number;
  meta?: Record<string, unknown>;
}

/** Informational, non-scoring annotation (e.g. Jev decision-model probabilities). Never affects the score. */
export interface MatchSignalDetail {
  key: string;
  type: 'SIGNAL';
  meta?: Record<string, unknown>;
}

export type MatchDetail = MatchFilterDetail | MatchScoreDetail | MatchSignalDetail;

export interface ScoreBreakdown {
  baseScore: number;
  totalPenalty: number;
  finalScore: number;
  totalWeight: number;
}

export interface MatchComputationResult {
  finalScore: number;
  eligible: boolean;
  eliminationReasons: string[];
  details: MatchDetail[];
  scoreBreakdown?: ScoreBreakdown;
}

/** Zorunlu yetenek semantic eşleşmesi: hangi pozison skill'i hangi aday skill'i ile karşılandı (örn. Java => Java 8) */
export interface SemanticSkillMatch {
  required: string;
  matchedBy: string;
}

export interface CandidateMatch {
  id: string;
  positionId: string;
  candidateId?: string;
  candidate: Candidate;
  matchScore: number;
  matchDetails: MatchDetail[] | null;
  scoreBreakdown?: ScoreBreakdown;
  /** Zorunlu yetenekler semantic eşleştiyse: [{ required: "Java", matchedBy: "Java 8" }] — UI'da "Java => Java 8" göstermek için */
  semanticSkillMatches?: SemanticSkillMatch[];
  eliminationReasons?: string[];
  eligible?: boolean;
  updatedAt: string;
  assigned?: boolean;
  pipelineStatus?: string | null;
}

export interface ContactPerson {
  name: string;
  surname: string;
  email: string;
  phone: string;
  title: string;
  isPrimary: boolean;
}

export interface CustomerCompany {
  id: string;
  name: string;
  type: 'CLIENT' | 'PARTNER' | 'SUPPLIER';
  status?: Status;
  industry: string;
  website: string;
  city: string;
  description: string;
  contactPerson: ContactPerson[];
  positions: JobPosition[];
  createdAt: Date;
  updatedAt: Date;
  size: string | null;
  district: string | null;
  foundedYear: number | null;
  employeeCount: number;
  revenue: number | null;
  deletedAt: Date | null;
  companyId: string;
}

export interface Rule {
  evaluatorKey: EvaluatorKey;
  type: ScoringRuleType;
  parameters: Record<string, unknown>;
  weight?: number;
}

export interface ParameterSchema {
  name: string;
  type: string;
  options?: string[];
  required: boolean;
  description: string;
  example: unknown;
}

export interface Evaluator {
  key: EvaluatorKey;
  name: string;
  description: string;
  compatibleTypes: string[];
  parameterSchema: ParameterSchema[];
  visibleInBuilder?: boolean;
  legacy?: boolean;
}

export interface Activities {
  history: ProcessHistory[];
  notes: Notes[];
}

export interface ActivityDetails {
  count?: number;
  message: string;
  companyId?: string;
  companyName?: string;
  candidateIds?: string[];
  positionName?: string;
  positionId?: string;
  from?: string;
  to?: string;
}

export interface EmployeeRef {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ProcessHistory {
  id: string;
  entityType: 'CANDIDATE' | 'POSITION' | 'EMPLOYEE' | 'EXPENSE';
  entityId: string;
  employeeId: string;
  type: HistoryType;
  action: string;
  content: string | null;
  details: ActivityDetails;
  mentions: { employeeIds: string[] } | null;
  createdAt: string;
  employee: EmployeeRef;
  date: string;
  category: NoteCategory;
}

export interface Notes {
  entityType: 'CANDIDATE' | 'POSITION' | 'EMPLOYEE';
  entityId: string;
  content: string;
  mentions: { employeeIds: string[] } | null;
  category: NoteCategory;
}

export interface ApprovalStatusDto {
  id: string;
  name: string;
  status: string;
  approvedAt?: Date | null;
}

export interface Expense {
  id: string;
  employeeId: string;
  amount: string;
  currency: string;
  expenseDate: string;
  description: string;
  rejectionReason: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
  documents: ExpenseDocument[];
  otherCategoryDescription?: string;
  category: ExpenseCategory;
  status: ExpenseStatus;
  companyId: string;
  activityLogs: ProcessHistory[];
  approvals?: ApprovalStatusDto[];
  teams?: { id: string; name: string }[];
}

export interface ExpenseDocument {
  id: string;
  expenseId: string;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
}

export type OvertimeRequest = {
  id: string;
  employee: Employee;
  date: string;
  startTime: string;
  endTime: string;
  reason: string;
  status: ApprovedStatusType;
  durationInMinutes: number;
  comments: string;
  rejectionReason: string;
  createdAt: string;
  updatedAt: string;
  employeeId: string;
  companyId: string;
  isLockedForPayroll: boolean;
  approvals?: ApprovalStatusDto[];
  team?: { id: string; name: string } | null;
};

export type WorkPlanEntry = {
  id: string;
  employeeId: string;
  companyId: string;
  date: string;
  startTime: string;
  endTime: string;
  durationInMinutes: number;
  type: WorkPlanEntryType;
  note?: string | null;
  source?: 'WORK_PLAN' | 'OVERTIME_REQUEST';
  sourceId?: string | null;
  isReadOnly?: boolean;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
  team?: { id: string; name: string } | null;
};

export type TimesheetMonth = {
  id: string;
  employeeId: string;
  companyId: string;
  month: string;
  isLocked: boolean;
  lockedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkPlanMonthSummary = {
  totalMinutes: number;
  totalEntries: number;
  activeDays: number;
  weekendDays: number;
  month: string;
  isLocked: boolean;
};

export interface NotificationContentDetails {
  translationKey?: string;
  translationParams?: Record<string, unknown>;
  rawText?: string;
}

export type NotificationContent =
  | NotificationContentDetails
  | (Record<string, unknown> & { details?: NotificationContentDetails })
  | string
  | null;

export interface Notification {
  id: string;
  content: NotificationContent;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  type: NotificationType;
  link?: string;
  userId: string;
  user?: User;
}

export interface NotificationState {
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
  fetchNotifications: (force?: boolean) => Promise<void>;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotifications: () => void;
}

export interface AnnouncementCategory {
  id: string;
  name: string;
  color?: string;
  description?: string;
  companyId?: string;
}

export interface AnnouncementAttachment {
  id?: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  fileType: string;
  objectKey?: string;
  data?: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  category: AnnouncementCategory;
  categoryId: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  isPinned: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  authorId: string;
  author: {
    firstName: string;
    lastName: string;
  };
  attachments?: AnnouncementAttachment[];
  isRead?: boolean;
  isBroadcast?: boolean;
  targetRoles?: RoleType[]; // --- YENİ ---
  targetGroups?: Array<{
    announcementId: string;
    groupId: string;
    organizationGroup: {
      name: string;
    };
  }>;
  targetUsers?: Array<{
    announcementId: string;
    userId: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
    };
  }>;
}

export type AnnouncementTargetUser = NonNullable<Announcement['targetUsers']>[number];

// =====================
// Generative UI Props Types
// =====================

export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string | string[];
}

export interface ChartProps {
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  title: string;
  data: {
    labels: string[];
    datasets: ChartDataset[];
  };
  summary?: string;
  error?: string;
}

export interface ComparisonTableRow {
  label: string;
  values: (string | number)[];
  highlight?: boolean;
}

export interface ComparisonTableProps {
  title: string;
  headers: string[];
  rows: ComparisonTableRow[];
  summary?: string;
  error?: string;
}

export interface TimelineEvent {
  id: string | number;
  date: string;
  title: string;
  description?: string;
  type?: 'success' | 'warning' | 'error' | 'info' | 'default';
  icon?: string;
}

export interface TimelineProps {
  title?: string;
  events: TimelineEvent[];
  summary?: string;
  error?: string;
}

export interface SkillMatrixItem {
  skillName: string;
  currentLevel: number;
  requiredLevel: number;
  gap?: number;
}

export interface SkillMatrixProps {
  title?: string;
  positionTitle?: string;
  items: SkillMatrixItem[];
  summary?: string;
  error?: string;
}

export interface SkillListItem {
  id: string;
  name: string;
  employeeCount: number;
}

export interface SkillListCardProps {
  skills: SkillListItem[];
  count: number;
  total: number;
  error?: string;
}

// =====================
// Hiring Funnel & Pipeline Types
// =====================

export interface HiringFunnelStage {
  status: string;
  statusLabel: string;
  count: number;
  percentage: number;
}

export interface HiringFunnelCardProps {
  positionTitle?: string;
  daysBack: number;
  totalCandidates: number;
  conversionRate: number;
  stages: HiringFunnelStage[];
  summary?: string;
  error?: string;
}

export interface PipelineCandidateItem {
  id: string;
  name: string;
  positionTitle?: string;
}

export interface PipelineStatusGroup {
  status: string;
  count: number;
  candidates: PipelineCandidateItem[];
}

export interface PipelineOverviewCardProps {
  positionFilter?: string | null;
  totalCandidates: number;
  statusBreakdown: PipelineStatusGroup[];
  summary?: string;
  error?: string;
}

export interface TrendSeriesPoint {
  x: string;
  y: number;
}

export interface TrendSeries {
  name: string;
  points: TrendSeriesPoint[];
}

export interface TrendChartCardProps {
  title: string;
  chartType: 'line' | 'area' | 'stacked_bar';
  series: TrendSeries[];
  xLabel?: string;
  yLabel?: string;
  summary?: string;
  error?: string;
}

// =====================
// Message Feedback Types
// =====================

export interface MessageFeedback {
  messageId: string;
  rating: 'positive' | 'negative';
  feedbackText?: string;
}

// =====================
// Card Interactivity Types
// =====================

/** Unique identifier for a card instance within a conversation */
export type CardInstanceId = string & { readonly __brand: 'CardInstanceId' };

/** Create a CardInstanceId from a string */
export function createCardInstanceId(id: string): CardInstanceId {
  return id as CardInstanceId;
}

/** Visual variants for card action buttons */
export type CardActionVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';

/** Confirmation dialog specification for destructive actions */
export interface CardConfirmSpec {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Base field properties shared by all field types */
interface CardFieldBase {
  name: string;
  label: string;
  required?: boolean;
  helpText?: string;
}

/** Text input field */
interface CardFieldText extends CardFieldBase {
  type: 'text';
  placeholder?: string;
  defaultValue?: string;
}

/** Textarea field for multi-line input */
interface CardFieldTextarea extends CardFieldBase {
  type: 'textarea';
  placeholder?: string;
  defaultValue?: string;
}

/** Number input field with optional min/max */
interface CardFieldNumber extends CardFieldBase {
  type: 'number';
  placeholder?: string;
  defaultValue?: number;
  min?: number;
  max?: number;
}

/** Select dropdown field with options */
interface CardFieldSelect extends CardFieldBase {
  type: 'select';
  placeholder?: string;
  defaultValue?: string;
  options: Array<{ label: string; value: string }>;
}

/** Date picker field */
interface CardFieldDate extends CardFieldBase {
  type: 'date';
  defaultValue?: string;
}

/** Date range picker field */
interface CardFieldDateRange extends CardFieldBase {
  type: 'date_range';
  defaultValue?: string;
}

/** Star rating field with optional max value */
interface CardFieldRating extends CardFieldBase {
  type: 'rating';
  defaultValue?: number;
  min?: number;
  max?: number;
}

/** Checkbox field */
interface CardFieldCheckbox extends CardFieldBase {
  type: 'checkbox';
  defaultValue?: boolean;
}

/** Field specification for quick forms - discriminated union by type */
export type CardFieldSpec =
  | CardFieldText
  | CardFieldTextarea
  | CardFieldNumber
  | CardFieldSelect
  | CardFieldDate
  | CardFieldDateRange
  | CardFieldRating
  | CardFieldCheckbox;

/** Supported field types for quick forms */
export type CardFieldType = CardFieldSpec['type'];

/** Input form specification for actions requiring user input */
export interface CardActionInputSpec {
  mode: 'inline' | 'popover' | 'dialog';
  title?: string;
  fields: CardFieldSpec[];
  submitLabel?: string;
}

/**
 * Action specification for interactive cards.
 * Defines buttons/actions that can be performed on card items.
 */
export interface CardActionSpec<TPayload = Record<string, unknown>> {
  /** Unique action identifier */
  id: string;
  /** Action type for routing (e.g., 'leave.approve', 'navigate.employee') */
  type: string;
  /** Display label for the action button */
  label: string;
  /** Lucide icon name (e.g., 'Check', 'X', 'ExternalLink') */
  icon?: string;
  /** Visual variant for the button */
  variant?: CardActionVariant;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Reason for being disabled (shown as tooltip) */
  disabledReason?: string;
  /** Confirmation dialog before executing */
  confirm?: CardConfirmSpec;
  /** Input form for collecting additional data */
  input?: CardActionInputSpec;
  /** Payload to pass to the action handler */
  payload?: TPayload;
}

/** Expand/collapse specification for expandable cards */
export interface CardExpandSpec {
  defaultExpanded?: boolean;
  labelCollapsed?: string;
  labelExpanded?: string;
}

/** Smart suggestion for follow-up actions */
export type SmartSuggestion =
  | { id: string; label: string; kind: 'chat'; message: string }
  | { id: string; label: string; kind: 'action'; action: CardActionSpec }
  | { id: string; label: string; kind: 'navigate'; to: string };

/**
 * UI configuration for tool result cards.
 * Enables interactive features like actions, expansion, and suggestions.
 */
export interface ToolCardUI {
  /** Unique card identifier for tracking state */
  cardId?: string;
  /** Available actions for this card */
  actions?: CardActionSpec[];
  /** Expand/collapse configuration */
  expand?: CardExpandSpec;
  /** Smart suggestions for follow-up */
  suggestions?: SmartSuggestion[];
  /** Whether the card is in loading state */
  isLoading?: boolean;
}

/** Helper type to add UI configuration to any card props */
export type WithToolCardUI<T> = T & { __ui?: ToolCardUI };

// =====================
// Action Execution Types
// =====================

/** Request to execute a card action */
export interface ExecuteActionRequest {
  conversationId: string;
  cardInstanceId: string;
  actionType: string;
  payload: Record<string, unknown>;
  input?: Record<string, unknown>;
}

/** Successful action execution response */
interface ExecuteActionResponseSuccess {
  ok: true;
  /** Partial update to apply to the card */
  cardPatch?: Record<string, unknown>;
  /** Toast notification to show */
  toast?: { type: 'success' | 'error' | 'warning'; message: string };
  /** Updated entities for cache invalidation */
  updatedEntities?: Array<{ type: string; id: string; data: unknown }>;
}

/** Failed action execution response */
interface ExecuteActionResponseError {
  ok: false;
  /** Error details */
  error: { code: string; message: string };
}

/** Response from action execution - discriminated union by ok field */
export type ExecuteActionResponse = ExecuteActionResponseSuccess | ExecuteActionResponseError;

export * from './types/core.js';
export * from './types/calendar.js';
export * from './types/permission.js';
export * from './types/leave.js';
export * from './types/expense.js';

// =====================
// Leave Form (depends on LeaveRequest above)
// =====================

import type { LeaveApproval } from './types/leave.js';
import type { EntityId } from './types/core.js';
export type LeaveWithApprovals = LeaveRequest & {
  approvals?: Array<LeaveApproval | EntityId>;
};
